import {useDispatch} from "react-redux";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {getAppDttm} from "../../store/ui/selectors";
import React, {useCallback, useMemo, useRef, useState} from "react";
import {setAppDate} from "../../store/ui/actions";
import {
  addDays,
  driverActivitiesSelector,
  DriverActivityEntity,
  extractApiErrorMessage,
  getObjectId,
  PersonEntity,
  startOfDay,
  subDays,
  useDriverActivityPrefetch,
  useGetDriverActivityByDayQuery
} from "@ots-frontend/ots-data";
import {Card, Container, Group, LoadingOverlay, Stack, Title} from "@mantine/core";
import {
  Badge,
  DriverActivityFilterFormModal,
  DriverActivityFilterFormValues,
  DriverActivityGrid,
  DriverActivityMenu,
  ResizeableContent,
  RouteBreadcrumbs,
  ThemedPage,
  useApp
} from "@ots-frontend/ots-ui";
import {skipToken} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDisclosure} from "@mantine/hooks";
import {
  getDriverActivityDayPageFilter,
  getDriverActivityDayPageHiddenPersons,
  getDriverActivityDayPageState
} from "../../store/driver-activity-day-page/selectors";
import {GridPreDestroyedEvent, GridReadyEvent, GridState, RowSelectedEvent} from "@ag-grid-community/core";
import {AgGridReact} from "@ag-grid-community/react";
import {notifications} from "@mantine/notifications";
import {
  setDriverActivityDayPageFilter,
  setDriverActivityDayPageState,
  toggleDriverActivityDayPagePerson
} from "../../store/driver-activity-day-page/actions";
import {DriverActivityDayToolbar} from "../../components/DriverActivityDayToolbar/DriverActivityDayToolbar";
import {useNavigate} from "react-router-dom";
import {IconUserBolt} from "@tabler/icons-react";

export type DriverActivityDayPageProps = {}

export const DriverActivityDayPage = (props: DriverActivityDayPageProps) => {
  const {} = props

  const appDttm = useAppSelector(getAppDttm);
  const filter = useAppSelector(getDriverActivityDayPageFilter)
  const gridState = useAppSelector(getDriverActivityDayPageState)
  const hiddenPersons = useAppSelector(getDriverActivityDayPageHiddenPersons)

  const [filterOpened, filterHandler] = useDisclosure(false);

  const dispatch = useDispatch();
  const {regionId, preferences,} = useApp();
  const {t} = useTranslation(["common"]);
  const navigate = useNavigate()

  const [initialState, setInitialState] = useState<GridState | undefined>(undefined)
  const [selectedRows, setSelectedRows] = useState<DriverActivityEntity[]>([])
  const gridRef = useRef<AgGridReact<DriverActivityEntity>>(null)

  const prefetchDriverActivity = useDriverActivityPrefetch("getDriverActivityByDay");

  const selectActivities = useMemo(() => {
    return driverActivitiesSelector()
  }, []);

  const queryArg = useMemo(() => {
    if (regionId) {
      const reportTime = preferences?.reportDefaultTime ?? 0
      const startShiftReport = preferences?.reportDefaultReportTimeField !== 'TRANSPORT'

      return {
        day: startOfDay(appDttm).getTime() + reportTime,
        regionId: regionId,
        startShiftReport: startShiftReport,
      }
    } else {
      return skipToken
    }
  }, [regionId, preferences?.reportDefaultTime, preferences?.reportDefaultReportTimeField, appDttm])

  const {activities, isFetching, error, refetch} = useGetDriverActivityByDayQuery(queryArg, {
    selectFromResult: (result) => ({
      ...result,
      activities: selectActivities(result, {
        personId: getObjectId(filter.person),
        withCVDs: false,
      }),
      isFetching: result.isFetching,
      error: result.error,
    })
  });

  const prefetchDttmRange = useCallback((date: Date) => {
    const rootDate = startOfDay(date);

    // prefetch the previous day
    if (regionId) {
      const reportTime = preferences?.reportDefaultTime ?? 0
      const startShiftReport = preferences?.reportDefaultReportTimeField !== 'TRANSPORT'

      prefetchDriverActivity({
        day: subDays(rootDate, 1).getTime() + reportTime,
        regionId: regionId,
        startShiftReport: startShiftReport,
      });

      // prefetch the next day
      prefetchDriverActivity({
        day: addDays(rootDate, 1).getTime() + reportTime,
        regionId: regionId,
        startShiftReport: startShiftReport,
      });
    }

  }, [preferences?.reportDefaultReportTimeField, preferences?.reportDefaultTime, prefetchDriverActivity, regionId]);

  const onDateChange = useCallback((date: Date) => {
    dispatch(setAppDate(date.getTime()));
    prefetchDttmRange(date);
  }, [dispatch, prefetchDttmRange]);

  if (error) {
    notifications.show({
      id: "driverActivityDayPageSearchError",
      title: t("common.error", "Error"),
      message: extractApiErrorMessage(error) ?? t("common.error", "Error"),
      color: "red",
      loading: false,
      autoClose: true,
    });
  } else {
    notifications.hide("driverActivityDayPageSearchError")
  }

  const onGridPreDestroyed = useCallback((event: GridPreDestroyedEvent<DriverActivityEntity>) => {
    dispatch(setDriverActivityDayPageState(event.state))
  }, [dispatch])

  const onGridReady = useCallback((event: GridReadyEvent<DriverActivityEntity>) => {
    setInitialState(gridState);
  }, [gridState])

  const onRowSelected = useCallback((event: RowSelectedEvent<DriverActivityEntity>) => {
    setSelectedRows((prev) => {
      const selections: DriverActivityEntity[] = [...prev]

      const data = event.data
      if (data) {
        const foundIndex = prev.findIndex(a => a.id === data.id)
        if (event.node.isSelected()) {
          if (foundIndex === -1) {
            selections.push(data)
          }
        } else {
          if (foundIndex >= 0) {
            selections.splice(foundIndex, 1)
          }
        }
      }


      return selections
    })
  }, [])

  const onPersonToggle = useCallback((person: PersonEntity) => {
    dispatch(toggleDriverActivityDayPagePerson(person.id))
  }, [dispatch])

  const onSearchClick = useCallback(() => {
    navigate("search")
  }, [navigate])

  const onFilterSubmit = useCallback((filter: DriverActivityFilterFormValues) => {
    dispatch(setDriverActivityDayPageFilter(filter))
    filterHandler.close()
  }, [dispatch, filterHandler])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Group spacing="xs">
                      <IconUserBolt/>
                      <Title order={4}>{t("common.page.driveractivity.title", "Driver Activity")}</Title>
                      <Badge color="blue">
                        Beta
                      </Badge>
                    </Group>
                    <DriverActivityMenu refetch={refetch} context={selectedRows}/>
                  </Group>
                  <DriverActivityDayToolbar
                    date={new Date(appDttm)}
                    onDateChange={onDateChange}
                    filter={filter}
                    onFilterClick={() => filterHandler.open()}
                    onSearchClick={onSearchClick}/>
                </Card>
                <LoadingOverlay visible={isFetching} overlayBlur={2}/>
                <DriverActivityGrid
                  ref={gridRef}
                  data={activities}
                  initialState={initialState}
                  onGridReady={onGridReady}
                  onGridPreDestroyed={onGridPreDestroyed}
                  onRowSelected={onRowSelected}
                  hiddenPersons={hiddenPersons}
                  onPersonToggle={onPersonToggle}
                />
              </Stack>

              <DriverActivityFilterFormModal
                title={t<string>("common.action.filter", "Filter", {ns: "common"})}
                opened={filterOpened}
                onClose={filterHandler.close}
                onSubmit={onFilterSubmit}
                defaultValues={{
                  ...filter
                }}
              />

            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  )
};
